import { useMemo, useState } from 'react';
import { upperCase } from 'lodash';
import styled from 'styled-components';
import { EI18nLanguageUI, useTranslation } from '@packages/utils';
import { Paragraph } from 'components/Base/Typography';
import { Row, Col } from 'components/Base/Grid';
import { RoutesPaths } from 'routes/Routes.types';
import {
  ageLevelCalccualtorSectionKey,
  registrationModalKey,
  stepTwoKey,
} from 'components/Shared/RegisterAction/RegistrationModal/RegistrationModal.data';
import { EEventType } from 'data/types/events.types';
import useRegistrationContext, {
  AGE_OPTIONS,
  LANGUAGE_LABELS,
  LANGUAGE_TRANSLATE,
} from '../../context/NewRegistrationModalContext';
import AgeGroupSuggestionBody from '../../../../AgeGroupSuggestionCard/AgeGroupSuggestionBody';
import LabeledInfo from '../LabelInfo/LabelInfo';
import {
  InfoWrapper,
  AgeTextWrapper,
} from '../RegistrationContent/RegistationContent.style';

const CalculateButton = styled.button`
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #0077a0;
  font-family: Lato, sans-serif;
`;

const RegistrationAgeCalculator = () => {
  const { eventDetails, registrationValues } = useRegistrationContext();
  const [calculatorOpen] = useState<boolean>(false);
  const { ageGroupOptions } = eventDetails;
  const { t: tRegistrationModal } = useTranslation({
    keyPrefix: registrationModalKey,
    lng: 'translate',
  });
  const { t: tAgeLevel } = useTranslation({
    keyPrefix: ageLevelCalccualtorSectionKey,
    lng: 'translate',
  });
  const { t: tStepTwo } = useTranslation({
    keyPrefix: stepTwoKey,
    lng: 'translate',
  });
  const translatedLanguage = useMemo(
    () =>
      LANGUAGE_TRANSLATE[registrationValues.userLanguage as EI18nLanguageUI],
    [registrationValues.userLanguage],
  );
  const calculatorEnabledEventTypes = new Set([
    EEventType.READY_FOR_KINDERGARTEN,
    EEventType.READY_FOR_MATH,
    EEventType.READY_FOR_KINDERGARTEN_SOCIAL_EMOTIONAL,
  ]);

  if (!calculatorEnabledEventTypes.has(eventDetails.eventType)) {
    return null;
  }

  const gotoAboutAndFAQs = () => {
    window.open(RoutesPaths.ABOUT, '_blank');
  };

  return (
    <InfoWrapper>
      <Row className="mb-2">
        <Col span={12}>
          <Paragraph>
            <LabeledInfo
              label={tStepTwo('AGES')}
              labelWidth="fit-content"
              align="center"
            >
              &nbsp;
              <AgeTextWrapper>
                {ageGroupOptions[translatedLanguage]
                  ?.map((age: any) => AGE_OPTIONS[age.value])
                  .join(', ')}
              </AgeTextWrapper>
            </LabeledInfo>
          </Paragraph>
        </Col>
        <Col span={12}>
          <Paragraph>
            <LabeledInfo
              label={tStepTwo('LANGUAGE')}
              labelWidth="fit-content"
              align="center"
            >
              <span className="ml-2">
                {tRegistrationModal(
                  upperCase(LANGUAGE_LABELS[registrationValues.userLanguage]),
                )}
              </span>
            </LabeledInfo>
          </Paragraph>
        </Col>
      </Row>
      {!calculatorOpen && calculatorEnabledEventTypes && (
        <Paragraph fontSize={14}>
          <CalculateButton type="button" onClick={gotoAboutAndFAQs}>
            {tStepTwo('CALCULATE_AGE_BUTTON')}
          </CalculateButton>
        </Paragraph>
      )}
      {calculatorOpen && (
        <>
          <Paragraph fontSize={14} fontWeight={600}>
            {tAgeLevel('TITLE_1')}
          </Paragraph>
          <Paragraph fontSize={14} className="mb-4">
            {tAgeLevel('TITLE_2')}
          </Paragraph>

          <AgeGroupSuggestionBody hideApplyFilter lng="translate" />
        </>
      )}
    </InfoWrapper>
  );
};

export default RegistrationAgeCalculator;
